import React, {FC} from 'react';
import {styled} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import InboxFlourish from '../../../../assets/images/basicsGuide/inboxFlourish.svg';
import RoutingArrow from '../../../../assets/images/basicsGuide/routingArrow.svg';
import RoutingFlourish from '../../../../assets/images/basicsGuide/routingFlourish.svg';
import RoutingLine from '../../../../assets/images/basicsGuide/routingLine.svg';
import {
  CardText,
  StyledInboxCard,
  StyledInboxIcon,
  StyledVisualContainer,
  StyledVisualDetailsText,
  StyledVisualLayout,
} from './onboardingBasicsGuideHelpers';

/*
 * Props.
 */

interface OnboardingBasicsGuideRoutingVisualProps {}

/*
 * Style.
 */

const StyledPriorityInboxIcon = styled(StyledInboxIcon)`
  color: #dd3c00;
`;

const StyledMessageIcon = styled(Icon)`
  transform: scale(${24 / 20});
  color: #808080;
`;

const StyledInboxFlourish = styled.img`
  position: absolute;
  top: 438px;
  left: 346px;
  z-index: 1;

  animation-name: routingVisualInboxFlourishFadeOut;
  animation-delay: 0;
  animation-duration: 200ms;
  animation-fill-mode: forwards;
  opacity: 1;

  @keyframes routingVisualInboxFlourishFadeOut {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
`;

const StyledSupportInboxCard = styled(StyledInboxCard)`
  animation-name: routingVisualMoveSupportInbox;
  animation-delay: 0;
  animation-duration: 800ms;
  animation-fill-mode: forwards;

  position: absolute;
  top: 454px;
  left: 197px;
  filter: drop-shadow(0px 0px 10px rgba(77, 237, 255, 1));

  @keyframes routingVisualMoveSupportInbox {
    0% {
      top: 454px;
      filter: drop-shadow(0px 0px 10px rgba(77, 237, 255, 1));
    }
    ${(200 / 800) * 100}% {
      top: 454px;
      filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.1));
    }
    100% {
      top: 248px;
      filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.1));
    }
  }
`;

const StyledPriorityInboxCard = styled(StyledInboxCard)`
  position: absolute;
  top: 454px;
  left: 197px;

  ${computeFadeInAnimation('priority-inbox', 1200, 400)}
`;

const StyledRoutingLine = styled.img`
  position: absolute;
  top: 345px;
  left: 270px;
  z-index: 0;

  ${computeFadeInAnimation('routing-line', 1200, 400)}
`;

const StyledMessageCard = styled.div`
  width: 48px;
  height: 48px;
  background: ${(p) => p.theme.greys.white};
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 372px;
  left: 247px;
  z-index: 1;

  animation-name: messageCardFadeIn;
  animation-delay: 1200ms;
  animation-duration: 400ms;
  animation-fill-mode: forwards;
  opacity: 0;
  filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.1));

  @keyframes messageCardFadeIn {
    0% {
      opacity: 0;
      filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.1));
    }

    100% {
      opacity: 1;
      filter: drop-shadow(0px 0px 10px rgba(77, 237, 255, 1));
    }
  }
`;

const StyledRoutingArrow = styled.img`
  position: absolute;
  top: 420px;
  left: 263px;
  z-index: 0;

  ${computeFadeInAnimation('routing-arrow', 1200, 400)}
`;

const StyledRoutingFlourish = styled.img`
  position: absolute;
  top: 358px;
  left: 231px;
  z-index: 1;

  ${computeFadeInAnimation('routing-flourish', 1200, 400)}
`;

const StyledRoutingVisualDetailsText = styled(StyledVisualDetailsText)`
  animation-delay: 1200ms;
  animation-duration: 400ms;
`;

/*
 * Component.
 */

export const OnboardingBasicsGuideRoutingVisual: FC<OnboardingBasicsGuideRoutingVisualProps> = () => (
  <StyledVisualContainer>
    <StyledInboxFlourish src={InboxFlourish} alt="Your Front inbox!" />
    <StyledSupportInboxCard>
      <StyledInboxIcon name="inboxFilled" />
      <CardText>Support</CardText>
    </StyledSupportInboxCard>
    <StyledRoutingFlourish src={RoutingFlourish} alt="Front message routing!" />
    <StyledRoutingLine src={RoutingLine} alt="Front message routing!" />
    <StyledMessageCard>
      <StyledMessageIcon name="channelEmail" />
    </StyledMessageCard>
    <StyledRoutingArrow src={RoutingArrow} alt="Front message routing!" />
    <StyledPriorityInboxCard>
      <StyledPriorityInboxIcon name="inboxFilled" />
      <CardText>Support - Priority</CardText>
    </StyledPriorityInboxCard>
    <StyledVisualLayout>
      <StyledRoutingVisualDetailsText>
        <Trans id="onboarding-basics-guide-routing-visual-details">
          You can <span>move</span> messages between inboxes manually or automatically. For example, complex
          issues in the Support inbox could be escalated to the Priority Support inbox.
        </Trans>
      </StyledRoutingVisualDetailsText>
    </StyledVisualLayout>
  </StyledVisualContainer>
);

function computeFadeInAnimation(slug: string, delay: number, duration: number) {
  const animationName = `fadeIn-${slug}`;
  return `
    animation-name: ${animationName};
    animation-delay: ${delay}ms;
    animation-duration: ${duration}ms;
    animation-fill-mode: forwards;
    opacity: 0;

    @keyframes ${animationName} {
      0% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }
  `;
}
